/* RESPONSIBLE TEAM: team-phone */
import { assetUrl } from '@intercom/pulse/helpers/asset-url';
import type {
  PhoneWorkerState,
  WorkerToTabMessage,
  HeartbeatConfig,
  TabSummary,
} from 'embercom/lib/phone/shared-worker-types';

type StateListener = (state: PhoneWorkerState) => void;
type HeartbeatListener = (
  healthy: boolean,
  lastSuccess: number | null,
  tabSummary: TabSummary,
) => void;
type ErrorListener = (error: string, context: string) => void;

export default class PhoneSharedWorkerClient {
  private worker: SharedWorker;
  private port: MessagePort;
  private tabId: string;
  private _currentState: PhoneWorkerState | null = null;

  private stateListeners = new Set<StateListener>();
  private heartbeatListeners = new Set<HeartbeatListener>();
  private errorListeners = new Set<ErrorListener>();

  constructor() {
    let rawUrl = assetUrl('/assets/workers/phone-shared-worker.js');
    let workerUrl = rawUrl.startsWith('http') ? new URL(rawUrl).pathname : rawUrl;
    this.worker = new SharedWorker(workerUrl, { name: 'intercom-phone' });
    this.port = this.worker.port;
    this.tabId = '';

    this.port.onmessage = (event: MessageEvent<WorkerToTabMessage>) => {
      this.handleMessage(event.data);
    };

    this.port.start();
  }

  connect(
    tabId: string,
    sessionId: string,
    adminId: string,
    appId: string,
    role: 'tab' | 'observer' = 'tab',
  ): void {
    this.tabId = tabId;
    this.port.postMessage({
      type: 'connect',
      tabId,
      sessionId,
      adminId,
      appId,
      role,
    });
  }

  disconnect(): void {
    this.port.postMessage({ type: 'disconnect', tabId: this.tabId });
    this.cleanup();
  }

  shutdown(): void {
    this.port.postMessage({ type: 'shutdown' });
    this.cleanup();
  }

  private cleanup(): void {
    this.stateListeners.clear();
    this.heartbeatListeners.clear();
    this.errorListeners.clear();
    this._currentState = null;
  }

  updateHeartbeatConfig(config: HeartbeatConfig): void {
    this.port.postMessage({ type: 'heartbeat-config', config });
  }

  updateCsrfToken(csrfToken: string): void {
    this.port.postMessage({ type: 'csrf-update', csrfToken });
  }

  updateDeviceState(deviceState: string): void {
    this.port.postMessage({ type: 'device-state-update', tabId: this.tabId, deviceState });
  }

  updateVisibility(visible: boolean, lastInteraction: number): void {
    this.port.postMessage({
      type: 'visibility-changed',
      tabId: this.tabId,
      visible,
      lastInteraction,
    });
  }

  requestState(): void {
    this.port.postMessage({ type: 'request-state', tabId: this.tabId });
  }

  get currentState(): PhoneWorkerState | null {
    return this._currentState;
  }

  onStateUpdate(listener: StateListener): () => void {
    this.stateListeners.add(listener);
    return () => this.stateListeners.delete(listener);
  }

  onHeartbeatStatus(listener: HeartbeatListener): () => void {
    this.heartbeatListeners.add(listener);
    return () => this.heartbeatListeners.delete(listener);
  }

  onError(listener: ErrorListener): () => void {
    this.errorListeners.add(listener);
    return () => this.errorListeners.delete(listener);
  }

  private handleMessage(message: WorkerToTabMessage): void {
    switch (message.type) {
      case 'state-update':
        this._currentState = message.state;
        for (let listener of this.stateListeners) {
          listener(message.state);
        }
        break;

      case 'heartbeat-status':
        for (let listener of this.heartbeatListeners) {
          listener(message.healthy, message.lastSuccess, message.tabSummary);
        }
        break;

      case 'error':
        for (let listener of this.errorListeners) {
          listener(message.error, message.context);
        }
        break;
    }
  }
}
